import React, { useEffect, useState } from 'react';
import { Loader2, GitMerge } from 'lucide-react';
import { Modal, Btn, INPUT, LABEL, TypeBadge, cx } from './ui';

const TYPE_NAME = { lead: 'Lead', active_client: 'Active client', approved: 'Approved', settled: 'Settled', past_client: 'Past client', contact: 'Contact' };
const useReset = (open, init) => { const [v, setV] = useState(init); useEffect(() => { if (open) setV(init); }, [open]); return [v, setV]; };

const Submit = ({ busy, disabled, onClick, children, testid }) => <Btn tone="solid" disabled={busy || disabled} onClick={onClick} data-testid={testid}>{busy && <Loader2 className="h-4 w-4 animate-spin" />}{children}</Btn>;

export const SmsDialog = ({ open, onClose, onSend, count, name }) => {
  const [body, setBody] = useReset(open, '');
  const [busy, setBusy] = useState(false);
  const segs = Math.max(1, Math.ceil(body.length / 160));
  return (
    <Modal open={open} onClose={onClose} title={count > 1 ? `Text ${count} contacts` : `Text ${name || 'contact'}`} testid="contact-sms-dialog"
      footer={<><Btn onClick={onClose}>Cancel</Btn><Submit busy={busy} disabled={!body.trim()} testid="contact-sms-send" onClick={async () => { setBusy(true); await onSend(body); setBusy(false); }}>Send SMS</Submit></>}>
      <textarea value={body} onChange={(e) => setBody(e.target.value.slice(0, 640))} rows={5} placeholder="Write your message" className={cx(INPUT, 'resize-none')} data-testid="contact-sms-body" />
      <p className="mt-1.5 text-xs text-[var(--muted)]">{body.length}/640 · {segs} SMS{count > 1 ? ` each · ${count * segs} total` : ''}. Sends a real text through ClickSend.</p>
    </Modal>
  );
};

export const NoteDialog = ({ open, onClose, onSave }) => {
  const [body, setBody] = useReset(open, '');
  const [busy, setBusy] = useState(false);
  return (
    <Modal open={open} onClose={onClose} title="Add a note" testid="contact-note-dialog"
      footer={<><Btn onClick={onClose}>Cancel</Btn><Submit busy={busy} disabled={!body.trim()} testid="contact-note-save" onClick={async () => { setBusy(true); await onSave(body); setBusy(false); }}>Save note</Submit></>}>
      <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={5} placeholder="What should the team know?" className={cx(INPUT, 'resize-none')} data-testid="contact-note-body" />
    </Modal>
  );
};

const plus = (h, day) => { const d = new Date(); if (day) { d.setDate(d.getDate() + day); d.setHours(9, 0, 0, 0); } else d.setTime(d.getTime() + h * 3600e3); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };
export const CallbackDialog = ({ open, onClose, onSave }) => {
  const [due, setDue] = useReset(open, plus(0, 1));
  const [note, setNote] = useReset(open, '');
  const [busy, setBusy] = useState(false);
  return (
    <Modal open={open} onClose={onClose} title="Book a callback" testid="contact-callback-dialog"
      footer={<><Btn onClick={onClose}>Cancel</Btn><Submit busy={busy} disabled={!due} testid="contact-callback-save" onClick={async () => { setBusy(true); await onSave(new Date(due).toISOString(), note); setBusy(false); }}>Book callback</Submit></>}>
      <div className="mb-2 flex flex-wrap gap-1.5">{[['In 1 hour', plus(1)], ['Tomorrow 9am', plus(0, 1)], ['Next week', plus(0, 7)]].map(([l, v]) => <Btn key={l} size="sm" onClick={() => setDue(v)}>{l}</Btn>)}</div>
      <input type="datetime-local" value={due} onChange={(e) => setDue(e.target.value)} className={INPUT} data-testid="contact-callback-due" />
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason (optional)" className={cx(INPUT, 'mt-2')} data-testid="contact-callback-note" />
      <p className="mt-2 text-xs text-[var(--muted)]">It appears in your Calling Queue callbacks and is prioritised when due.</p>
    </Modal>
  );
};

export const AssignDialog = ({ open, onClose, onSave, team, count }) => {
  const [uid, setUid] = useReset(open, '');
  const [busy, setBusy] = useState(false);
  return (
    <Modal open={open} onClose={onClose} title={count > 1 ? `Assign ${count} contacts` : 'Assign a team member'} testid="contact-assign-dialog"
      footer={<><Btn onClick={onClose}>Cancel</Btn><Submit busy={busy} disabled={!uid} testid="contact-assign-save" onClick={async () => { setBusy(true); await onSave(uid); setBusy(false); }}>Assign</Submit></>}>
      <select value={uid} onChange={(e) => setUid(e.target.value)} className={INPUT} data-testid="contact-assign-select">
        <option value="">Choose a team member</option>
        {team.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
      </select>
      <p className="mt-2 text-xs text-[var(--muted)]">Linked leads move to this person too. Client portal files keep their broker.</p>
    </Modal>
  );
};

export const EditDialog = ({ open, onClose, onSave, contact }) => {
  const [v, setV] = useReset(open, { name: contact?.name || '', mobile: contact?.mobile || '', email: contact?.email || '' });
  const [busy, setBusy] = useState(false);
  return (
    <Modal open={open} onClose={onClose} title="Edit contact" testid="contact-edit-dialog"
      footer={<><Btn onClick={onClose}>Cancel</Btn><Submit busy={busy} disabled={!v.name.trim()} testid="contact-edit-save" onClick={async () => { setBusy(true); await onSave(v); setBusy(false); }}>Save changes</Submit></>}>
      {[['name', 'Full name'], ['mobile', 'Mobile'], ['email', 'Email']].map(([k, l]) => (
        <label key={k} className="mb-3 block"><span className={LABEL}>{l}</span><input value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} className={INPUT} data-testid={`contact-edit-${k}`} /></label>))}
      <p className="text-xs text-[var(--muted)]">Changes update the linked lead, client portal and settled loan records. Old values are kept in the audit log.</p>
    </Modal>
  );
};

export const MergeDialog = ({ open, onClose, onMerge, group }) => {
  const [primary, setPrimary] = useReset(open, group?.[0]?.id);
  const [busy, setBusy] = useState(false);
  return (
    <Modal open={open} onClose={onClose} title="Merge duplicate contacts" wide testid="contact-merge-dialog"
      footer={<><Btn onClick={onClose}>Cancel</Btn><Submit busy={busy} disabled={!primary} testid="contact-merge-confirm" onClick={async () => { setBusy(true); await onMerge(primary, (group || []).filter((c) => c.id !== primary).map((c) => c.id)); setBusy(false); }}><GitMerge className="h-4 w-4" />Merge into one</Submit></>}>
      <p className="mb-3 text-sm text-[var(--muted)]">Pick the record to keep. Every note, message, call and application from the others moves onto it. Nothing is deleted.</p>
      <ul className="space-y-1.5">
        {(group || []).map((c) => (
          <li key={c.id}><label className={cx('flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-2.5', primary === c.id ? 'border-[var(--accent)] bg-[var(--accent-soft)]' : 'border-[var(--line)]')} data-testid={`contact-merge-option-${c.id}`}>
            <input type="radio" checked={primary === c.id} onChange={() => setPrimary(c.id)} />
            <span className="min-w-0 flex-1"><span className="block text-sm font-semibold text-[var(--ink)]">{c.name}</span><span className="block truncate text-xs text-[var(--muted)]">{[c.mobile, c.email, c.status_label].filter(Boolean).join(' · ')}</span></span>
            <TypeBadge type={c.type} label={TYPE_NAME[c.type] || 'Contact'} />
          </label></li>))}
      </ul>
    </Modal>
  );
};
